import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FormattedMessage, useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';

import { useDeleteMembershipType } from 'src/features/membershipType/api/useDeleteMembershipType';
import { LIST_MEMBERSHIP_TYPES_RESOURCE } from 'src/features/membershipType/api/useListMembershipTypes';
import ActivateMembershipType from 'src/features/membershipType/components/forms/ActivateMembershipType';
import CopyMembershipTypeFormModal from 'src/features/membershipType/components/forms/CopyMembershipTypeFormModal';
import DeactivateMembershipType from 'src/features/membershipType/components/forms/DeactivateMembershipType';
import DeleteMembershipType from 'src/features/membershipType/components/forms/DeleteMembershipType';
import UpdateMembershipTypeFormModal from 'src/features/membershipType/components/forms/UpdateMembershipTypeFormModal';
import { MembershipType } from 'src/features/membershipType/types';
import { membershipTypeMessages } from 'src/features/membershipType/utils/messages';
import { PATH } from 'src/features/navigation/utils/constants';
import { toast } from 'src/hooks/UseToast';
import { queryClient } from 'src/lib/react-query';
import { Button } from 'src/registry/ui/Button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from 'src/registry/ui/Tooltip';
import { generalMessages } from 'src/registry/utils/messages';
import { ICON_MAP } from 'src/utils/constants';
import { useOnError } from 'src/utils/errors';
import { commonMessages } from 'src/utils/messages';

type MembershipTypeActionButtonsProps = {
    membershipTemplate: MembershipType;
    isSystemMembershipTemplate: boolean;
    hasMemberships: boolean;
};

const MembershipTypeActionButtons = ({
    membershipTemplate,
    isSystemMembershipTemplate,
    hasMemberships,
}: MembershipTypeActionButtonsProps) => {
    const { formatMessage, locale } = useIntl();
    const navigate = useNavigate();
    const onError = useOnError();

    const { mutate: deleteTemplate } = useDeleteMembershipType({
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: [LIST_MEMBERSHIP_TYPES_RESOURCE] });
            void navigate(`/${PATH.ADMINISTRATION}/membership-templates`);
            toast({
                title: formatMessage(membershipTypeMessages.deleteTemplateSuccess),
                description: formatMessage(membershipTypeMessages.deleteTemplateSuccessDescription, {
                    entry: membershipTemplate?.translations.find((translation) => translation.language === locale)
                        ?.name,
                }),
            });
        },
        onError: (error) => {
            onError(error, () => null, { inlineErrorStatuses: [] });
        },
    });

    return (
        <div className="flex flex-row gap-2">
            {!membershipTemplate.is_active && !isSystemMembershipTemplate && (
                <ActivateMembershipType selectedTemplate={membershipTemplate}>
                    <Button size="mdIcon" disabled={!membershipTemplate?.can_be_activated}>
                        <FontAwesomeIcon icon={ICON_MAP.WAVE_PULSE} />
                        <span className="hidden lg:block">
                            {formatMessage(membershipTypeMessages.activateTemplate)}
                        </span>
                    </Button>
                </ActivateMembershipType>
            )}
            {membershipTemplate.is_active && !isSystemMembershipTemplate && (
                <DeactivateMembershipType selectedTemplate={membershipTemplate}>
                    <Button size="mdIcon" variant="destructiveLight">
                        <FontAwesomeIcon icon={ICON_MAP.WAVE_PULSE} />
                        <span className="hidden lg:block">
                            {formatMessage(membershipTypeMessages.deactivateTemplate)}
                        </span>
                    </Button>
                </DeactivateMembershipType>
            )}

            <CopyMembershipTypeFormModal selectedMembershipTemplate={membershipTemplate}>
                <Button variant="default" size="mdIcon" className="cursor-pointer">
                    <FontAwesomeIcon icon={ICON_MAP.COPY} />
                    <span className="hidden lg:block">{formatMessage(generalMessages.copy)}</span>
                </Button>
            </CopyMembershipTypeFormModal>

            <TooltipProvider>
                {!isSystemMembershipTemplate && (
                    <>
                        <UpdateMembershipTypeFormModal selectedMembershipTemplate={membershipTemplate}>
                            <Button size="mdIcon" variant="outline">
                                <FontAwesomeIcon icon={ICON_MAP.EDIT} />
                                <span className="hidden lg:block">{formatMessage(commonMessages.edit)}</span>
                            </Button>
                        </UpdateMembershipTypeFormModal>

                        <Tooltip>
                            <TooltipTrigger asChild>
                                <span className="inline-block">
                                    <DeleteMembershipType
                                        selectedTemplate={membershipTemplate}
                                        deleteTemplate={deleteTemplate}
                                    >
                                        <Button size="mdIcon" variant="destructiveLight" disabled={hasMemberships}>
                                            <FontAwesomeIcon icon={ICON_MAP.DELETE} />
                                            <span className="hidden lg:block">
                                                {formatMessage(generalMessages.delete)}
                                            </span>
                                        </Button>
                                    </DeleteMembershipType>
                                </span>
                            </TooltipTrigger>
                            {hasMemberships && (
                                <TooltipContent side="top" className="max-w-56">
                                    <FormattedMessage {...membershipTypeMessages.nonDeletableMembershipTypeError} />
                                </TooltipContent>
                            )}
                        </Tooltip>
                    </>
                )}
            </TooltipProvider>
        </div>
    );
};

export default MembershipTypeActionButtons;
